import {
  useAgencyExchangeRates,
  useUpsertAgencyExchangeRate,
  useUseMarketExchangeRate,
} from '@/api/agency-exchange-rates.api';
import StyledButton from '@/components/styled/StyledButton';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useMemo, useState } from 'react';

const formatRate = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const DistributorProfileExchangeRatesTab = () => {
  const { t } = useTranslation();
  const { toastAsync } = useToast();
  const { data, isLoading } = useAgencyExchangeRates();
  const { mutateAsync: upsertRate } = useUpsertAgencyExchangeRate();
  const { mutateAsync: resetToMarket } = useUseMarketExchangeRate();
  const [draftRates, setDraftRates] = useState<Record<string, string>>({});

  const rows = useMemo(() => data?.rates ?? [], [data?.rates]);

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        {t('common.loading')}
      </div>
    );
  }

  if (!data || rows.length === 0) {
    return (
      <div className="p-6 text-sm text-gray-500">
        {t('pages.profile.exchangeRates.empty')}
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col gap-4">
      <p className="text-sm text-gray-600">
        {t('pages.profile.exchangeRates.description', {
          baseCurrency: data.baseCurrency,
        })}
      </p>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">
                {t('pages.profile.exchangeRates.columns.pair')}
              </th>
              <th className="px-4 py-3 font-medium">
                {t('pages.profile.exchangeRates.columns.marketRate')}
              </th>
              <th className="px-4 py-3 font-medium">
                {t('pages.profile.exchangeRates.columns.customRate')}
              </th>
              <th className="px-4 py-3 font-medium">
                {t('pages.profile.exchangeRates.columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const pairLabel = `${row.baseCurrency}/${row.quoteCurrency}`;
              const draftValue =
                draftRates[row.quoteCurrency] ??
                (row.rate != null ? String(row.rate) : '');
              const displayRate = row.usesMarketRate
                ? row.marketRate
                : (row.rate ?? row.marketRate);

              return (
                <tr key={row.quoteCurrency} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium">{pairLabel}</td>
                  <td className="px-4 py-3">{formatRate(row.marketRate)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <input
                        type="number"
                        min={0.0001}
                        step="0.0001"
                        value={draftValue}
                        placeholder={formatRate(row.marketRate)}
                        className="w-36 rounded-md border border-gray-300 px-3 py-2"
                        onChange={(event) =>
                          setDraftRates((current) => ({
                            ...current,
                            [row.quoteCurrency]: event.target.value,
                          }))
                        }
                      />
                      <span className="text-xs text-gray-500">
                        {row.usesMarketRate
                          ? t('pages.profile.exchangeRates.usingMarketRate')
                          : t('pages.profile.exchangeRates.effectiveRate', {
                              rate: formatRate(displayRate),
                            })}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <StyledButton
                        title={t('pages.profile.exchangeRates.save')}
                        className="px-3 py-2 text-sm"
                        onClick={() => {
                          const parsed = Number(draftValue);
                          if (!Number.isFinite(parsed) || parsed <= 0) return;
                          toastAsync(
                            upsertRate({
                              quoteCurrency: row.quoteCurrency,
                              rate: parsed,
                            }),
                            {
                              success: t(
                                'pages.profile.exchangeRates.savedSuccess',
                              ),
                            },
                          ).then(() => {
                            setDraftRates((current) => {
                              const next = { ...current };
                              delete next[row.quoteCurrency];
                              return next;
                            });
                          });
                        }}
                      />
                      <StyledButton
                        title={t('pages.profile.exchangeRates.useMarketRate')}
                        variant="outlined"
                        className="px-3 py-2 text-sm"
                        disabled={row.usesMarketRate}
                        onClick={() => {
                          toastAsync(
                            resetToMarket(row.quoteCurrency),
                            {
                              success: t(
                                'pages.profile.exchangeRates.marketRestoredSuccess',
                              ),
                            },
                          ).then(() => {
                            setDraftRates((current) => {
                              const next = { ...current };
                              delete next[row.quoteCurrency];
                              return next;
                            });
                          });
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DistributorProfileExchangeRatesTab;
